// Modules
import { useEffect } from "react";

// Redux
import { numberElementsPerPageSelector } from "redux/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { currentPageSelector, pageCountSelector, setCurrentPage, setForcePage, setPageCount } from "redux/pagination/paginationSlice";
import { suggestionsSearchResultSelector } from "redux/searchBar/searchBarSlice";

// Hooks
import useRouting from "hooks/useRouting";

// Constants
import { NUMBER_ELEMENTS_PER_PAGE } from "constants/results";
import { QueryStringPrimaryKeys } from "constants/urlEnum";

// Types
import { OnClickPaginator } from "interfaces/pagination";

// Utils
import { parsePageNumberFromUrl } from "utils/pagination";
import { changeParameterInQueryString } from "utils/queryString";

const usePagination = (urlPath: string) => {
	const dispatch = useAppDispatch();
	const pushNewRoute = useRouting();
	const numberTotalRows = useAppSelector(suggestionsSearchResultSelector).totalRows;
	const numberElementsPerPage = useAppSelector(numberElementsPerPageSelector);
	const pageCount = useAppSelector(pageCountSelector);
	const currentPage = useAppSelector(currentPageSelector);

	useEffect(() => {
		const pageNumber = parsePageNumberFromUrl(urlPath);
		const updatedPageCount = Math.ceil(numberTotalRows / (numberElementsPerPage || NUMBER_ELEMENTS_PER_PAGE));
		dispatch(setCurrentPage(pageNumber));
		dispatch(setForcePage(pageNumber));
		dispatch(setPageCount(updatedPageCount));
	}, [numberTotalRows, numberElementsPerPage, pageCount, urlPath]);

	const onClickPagination = (numberPage: number | OnClickPaginator): void => {
		if (typeof numberPage !== "number" && numberPage.nextSelectedPage !== undefined) {
			dispatch(setForcePage(undefined));
			changePagination(numberPage.nextSelectedPage);
		}
	};

	const changePagination = (nextNumberPage: number) => {
		dispatch(setCurrentPage(nextNumberPage));
		const nextUrl = changeParameterInQueryString(urlPath, QueryStringPrimaryKeys.PAGE, nextNumberPage + 1);
		pushNewRoute(nextUrl);
		window.scrollTo(0, 0);
	};

	return {
		pageCount,
		currentPage,
		onClickPagination
	};
};

export default usePagination;
